import React, { useEffect, useState } from "react";
import api, { API_BASE, extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CASE_STATUSES } from "@/lib/constants";
import { BarChart3, Download } from "lucide-react";
import { toast } from "sonner";

const Table = ({ title, obj }) => (
  <Card className="p-4">
    <h3 className="font-semibold mb-2">{title}</h3>
    <table className="w-full text-sm"><tbody>
      {Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
        <tr key={k} className="border-t"><td className="py-1.5 text-slate-600">{k}</td><td className="py-1.5 text-right font-medium">{v}</td></tr>
      ))}
      {Object.keys(obj || {}).length === 0 && <tr><td className="py-3 text-muted-foreground text-center" colSpan={2}>Nessun dato</td></tr>}
    </tbody></table>
  </Card>
);

const Reports = () => {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [f, setF] = useState({ dal: "", al: "", servizio_id: "", stato: "" });

  useEffect(() => { api.get("/services").then(({ data }) => setServices(data)).catch(() => {}); }, []);
  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/reports/cases?${p.toString()}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = async () => {
    try {
      const p = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v && k !== "servizio_id") p.set(k, v); });
      if (f.servizio_id) p.set("servizio_id", f.servizio_id);
      const r = await api.get(`/reports/cases/export?${p.toString()}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "report_pratiche.csv"; a.click();
      toast.success("Esportazione avviata");
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div>
      <PageHeader icon={BarChart3} title="Report" description="Statistiche ed esportazioni sulle pratiche"
        actions={can("reports.export") && <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-2" /> Esporta CSV</Button>} />
      <Card className="p-4 mb-4">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div><Label>Dal</Label><Input type="date" value={f.dal} onChange={(e) => setF({ ...f, dal: e.target.value })} /></div>
          <div><Label>Al</Label><Input type="date" value={f.al} onChange={(e) => setF({ ...f, al: e.target.value })} /></div>
          <div><Label>Servizio</Label><Select value={f.servizio_id || "tutti"} onValueChange={(v) => setF({ ...f, servizio_id: v === "tutti" ? "" : v })}><SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger><SelectContent className="max-h-64"><SelectItem value="tutti">Tutti</SelectItem>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex gap-2"><Select value={f.stato || "tutti"} onValueChange={(v) => setF({ ...f, stato: v === "tutti" ? "" : v })}><SelectTrigger><SelectValue placeholder="Stato" /></SelectTrigger><SelectContent><SelectItem value="tutti">Tutti</SelectItem>{CASE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select><Button onClick={load}>Applica</Button></div>
        </div>
      </Card>
      {loading ? <LoadingState /> : data && (
        <>
          <Card className="p-4 mb-4"><div className="text-sm text-muted-foreground">Totale pratiche nel periodo</div><div className="font-display text-3xl font-semibold">{data.totale}</div></Card>
          <div className="grid md:grid-cols-2 gap-4">
            <Table title="Per stato" obj={data.per_stato} />
            <Table title="Per servizio" obj={data.per_servizio} />
            <Table title="Per priorità" obj={data.per_priorita} />
            <Table title="Per origine del contatto" obj={data.per_origine} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-4">I dati rispettano i permessi dell'utente e il principio di minimizzazione. Ogni esportazione viene tracciata nel registro attività.</p>
        </>
      )}
    </div>
  );
};

export default Reports;
